/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR151: Deal Proof Section Component (Admin Only)
 * 
 * Displays policy and layout proof information in the admin deal details panel.
 * Shows: active layout preset, snapshot id, policy config, and key card-render fields.
 */

import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Shield, 
  Layout, 
  Database, 
  FileCheck, 
  Tag,
  Info,
  Clock,
} from "lucide-react";
import type { Deal } from "@/lib/types/deals";
import type { PreviewDeal } from "@/hooks/useAdminPreviewDeals";

interface DealProofSectionProps {
  deal: Deal & Partial<PreviewDeal>;
  layoutPresetName?: string | null;
  layoutEnvironment?: "staging" | "publish" | null;
  snapshotId?: string | null;
}

export function DealProofSection({ 
  deal, 
  layoutPresetName,
  layoutEnvironment,
  snapshotId,
}: DealProofSectionProps) {
  // Extract policy proof fields (may be on PreviewDeal extended interface)
  const policyConfigId = (deal as any).policy_config_id || null;
  const policyVersion = (deal as any).policy_version || null;
  const policyLoadedAt = (deal as any).policy_loaded_at || null;
  const eligibilityScopeReason = (deal as any).eligibility_scope_reason || null;
  const ruleIdsFired = (deal as any).eligibility_scope_rule_ids_fired || [];
  const evidenceSnippets = (deal as any).eligibility_scope_evidence_snippets || [];
  
  const hasPolicyProof = policyConfigId || policyVersion || eligibilityScopeReason;
  const hasLayoutProof = layoutPresetName || snapshotId;
  
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-semibold flex items-center gap-1.5">
        <Shield className="w-4 h-4" />
        Proof & Debug Info
      </h4>
      
      {/* Layout Configuration */}
      {hasLayoutProof && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Layout className="w-3 h-3" />
            Layout Configuration
          </div>
          <div className="bg-muted/50 rounded-md p-2 space-y-1 text-xs font-mono">
            {layoutPresetName && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">preset:</span>
                <span className="text-primary font-semibold">"{layoutPresetName}"</span>
              </div>
            )}
            {layoutEnvironment && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">env:</span>
                <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                  {layoutEnvironment}
                </Badge>
              </div>
            )}
            {snapshotId && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">snapshot_id:</span>
                <span className="truncate max-w-[160px]" title={snapshotId}>
                  {snapshotId.slice(0, 12)}...
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {hasLayoutProof && hasPolicyProof && <Separator />}
      
      {/* Policy Configuration */}
      {hasPolicyProof && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <FileCheck className="w-3 h-3" />
            Deal Policy Config
          </div>
          <div className="bg-muted/50 rounded-md p-2 space-y-1 text-xs font-mono">
            {policyConfigId && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">config_id:</span>
                <span className="truncate max-w-[140px]" title={policyConfigId}>
                  {policyConfigId.slice(0, 8)}...
                </span>
              </div>
            )}
            {policyVersion && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">version:</span>
                <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
                  {policyVersion}
                </Badge>
              </div>
            )}
            {policyLoadedAt && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">loaded_at:</span>
                <span className="text-[10px] flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5" />
                  {new Date(policyLoadedAt).toLocaleTimeString("en-CA", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Eligibility Scope Reason */}
      {eligibilityScopeReason && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Info className="w-3 h-3" />
            Eligibility Inference
          </div>
          <div className="bg-warning/10 border border-warning/30 rounded-md p-2 text-xs">
            <p className="font-medium text-warning-foreground">
              {eligibilityScopeReason}
            </p>
            {ruleIdsFired.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {ruleIdsFired.map((ruleId: string, idx: number) => (
                  <Badge key={idx} variant="outline" className="text-[10px] h-4 px-1.5">
                    {ruleId}
                  </Badge>
                ))}
              </div>
            )}
            {evidenceSnippets.length > 0 && (
              <div className="mt-2 space-y-1">
                <span className="text-muted-foreground text-[10px]">Evidence:</span>
                {evidenceSnippets.slice(0, 2).map((snippet: string, idx: number) => (
                  <p key={idx} className="text-[10px] text-muted-foreground italic truncate">
                    "{snippet}"
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      
      <Separator />
      
      {/* Card Render Fields */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Tag className="w-3 h-3" />
          Card Render Fields
        </div>
        <div className="bg-muted/50 rounded-md p-2 space-y-1 text-xs font-mono">
          <div className="flex justify-between">
            <span className="text-muted-foreground">deal_type:</span>
            <Badge variant="default" className="text-[10px] h-4 px-1.5">
              {deal.deal_category || deal.deal_type || "—"}
            </Badge>
          </div>
          {deal.discount_percent && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">discount_pct:</span>
              <span>{deal.discount_percent}%</span>
            </div>
          )}
          {deal.bonus_value_cad && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">bonus_value:</span>
              <span>${deal.bonus_value_cad.toFixed(2)}</span>
            </div>
          )}
          {deal.qualifying_spend_cad && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">spend_req:</span>
              <span>${deal.qualifying_spend_cad.toFixed(2)}</span>
            </div>
          )}
          {deal.computed_value_cad && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">computed_val:</span>
              <span className="text-primary font-semibold">${deal.computed_value_cad.toFixed(2)}</span>
            </div>
          )}
          {deal.eligibility_scope && deal.eligibility_scope !== "PUBLIC" && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">eligibility:</span>
              <Badge variant="outline" className="text-[10px] h-4 px-1.5 bg-muted text-muted-foreground">
                {deal.eligibility_scope}
              </Badge>
            </div>
          )}
        </div>
      </div>
      
      {/* Dedupe Key */}
      {(deal as any).dedupe_key && (
        <div className="mt-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Database className="w-3 h-3" />
            Dedupe Key
          </div>
          <p className="text-[10px] font-mono text-muted-foreground mt-1 break-all bg-muted/30 p-1.5 rounded">
            {(deal as any).dedupe_key}
          </p>
        </div>
      )}
    </div>
  );
}

export default DealProofSection;
